/**
 * Seat controllers — who decides what a seat does.
 *
 * Today there are two: a bot, and the human sitting at this browser. Later there is a
 * third, a human on the other end of a socket. The point of this interface is that
 * adding the third one touches nothing in the hand state machine.
 *
 * ## Why the engine stays synchronous
 *
 * The obvious design is `decide(): Promise<Action>` all the way down, and it is wrong
 * here. `engine/` is deterministic and I/O-free, and the fuzz tests, the replay tests
 * and the whole provably-fair story depend on that: the same seed must reproduce the
 * same session exactly, which it cannot if the engine awaits anything.
 *
 * So the split is:
 *
 *   - The ENGINE stays synchronous. It answers "who is to act and what may they do"
 *     and it accepts "this seat did that". It never waits for anyone.
 *   - The DRIVER, above the engine, is async. It asks a controller for an action,
 *     waits however long that takes, and hands the result back to the engine.
 *
 * A bot controller resolves immediately because the bot policy is a pure function.
 * A human controller resolves when a button is clicked or a socket message arrives.
 * The engine cannot tell the difference and does not need to.
 *
 * ## Timers
 *
 * Turn timeouts live here, not in the engine, for the same reason: a timeout is
 * wall-clock, and wall-clock in the engine would make a replay depend on how fast the
 * machine replaying it happens to be.
 */
import type { Action } from "./types";
import type { TableState } from "./table-state";

export type ControllerKind = "bot" | "local_human" | "remote_human";

export interface DecisionRequest {
  seat: number;
  /** The table as this seat is entitled to see it. */
  state: TableState;
  /** Every action that is legal right now. A controller may return only these. */
  options: Action[];
}

export interface SeatController {
  readonly kind: ControllerKind;
  /**
   * Choose an action.
   *
   * May return synchronously (bots do) or asynchronously (humans do). The driver
   * awaits either way.
   */
  decide(req: DecisionRequest): Action | Promise<Action>;
  /**
   * How long this seat gets before the driver acts for it, or null for no limit.
   *
   * Generous by design for the local human — a training tool that rushes someone
   * through a decision is working against its own purpose. It exists now mainly so
   * the UI pattern is in place before multiplayer needs it to be strict.
   */
  readonly timeoutMs: number | null;
  /** Called when a decision is no longer needed, e.g. the hand ended. */
  cancel?(): void;
}

/** What the driver does when a controller runs out of time. */
export function timeoutAction(options: Action[]): Action {
  const check = options.find((o) => o.kind === "check");
  if (check) return check;
  const fold = options.find((o) => o.kind === "fold");
  if (fold) return fold;
  // Neither available should be impossible; returning the first legal action is
  // better than throwing inside a timer nobody is awaiting.
  return options[0]!;
}

/**
 * A controller whose decision arrives later, from outside.
 *
 * This is the shape of BOTH the local human and, later, the remote one — the only
 * difference is what calls `supply`: a click handler here, a socket message there.
 * Writing it once is most of what makes the multiplayer port a transport change.
 */
export class DeferredController implements SeatController {
  private pending: ((a: Action) => void) | null = null;
  private request: DecisionRequest | null = null;

  constructor(
    readonly kind: ControllerKind = "local_human",
    readonly timeoutMs: number | null = 120_000,
    /** Notified when a decision is wanted, so the UI can light up. */
    private readonly onRequest?: (req: DecisionRequest | null) => void,
  ) {}

  decide(req: DecisionRequest): Promise<Action> {
    this.request = req;
    this.onRequest?.(req);
    return new Promise<Action>((resolve) => { this.pending = resolve; });
  }

  /** The action this seat has chosen. Ignored if nothing is waiting on it. */
  supply(action: Action): boolean {
    const resolve = this.pending;
    if (!resolve) return false;
    this.pending = null;
    this.request = null;
    this.onRequest?.(null);
    resolve(action);
    return true;
  }

  /** What is being asked of this seat right now, or null. */
  current(): DecisionRequest | null { return this.request; }

  cancel(): void {
    const resolve = this.pending;
    const req = this.request;
    this.pending = null;
    this.request = null;
    this.onRequest?.(null);
    // Resolve rather than reject: a cancelled decision is an ordinary event, and an
    // unhandled rejection in a hand that simply ended is noise, not a signal.
    if (resolve && req) resolve(timeoutAction(req.options));
  }
}

/**
 * A seat played by the machine.
 *
 * Thin on purpose. The bot's actual decision lives in `bot/policy.ts` and is a pure
 * function of the hand, the profile and a seeded RNG; this only adapts its shape.
 * Keeping it thin is what lets the engine call the same policy directly during fuzz
 * runs, where there is no driver and no controllers at all.
 */
export class BotController implements SeatController {
  readonly kind = "bot" as const;
  readonly timeoutMs = null;
  constructor(private readonly choose: (req: DecisionRequest) => Action) {}
  decide(req: DecisionRequest): Action { return this.choose(req); }
}
